// TypeScript mirrors of the Go domain types exposed by /api/dpp/*.
// The Go structs carry no `json` tags, so encoding/json serialises their
// exported (PascalCase) field names verbatim — these interfaces match that.

export type MemberRole = "admin" | "editor" | "viewer" | "supplier";

export interface Organization {
  ID: string;
  Name: string;
  OperatorIdentifier: string;
  PlanID: string;
  Suspended: boolean;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface Membership {
  ID: string;
  OrgID: string;
  UserID: string;
  Role: MemberRole;
  CreatedAt: string;
}

export interface Invitation {
  ID: string;
  OrgID: string;
  Email: string;
  Role: MemberRole;
  Token: string;
  Accepted: boolean;
  ExpiresAt: string;
  CreatedAt: string;
}

export interface Product {
  ID: string;
  OrgID: string;
  Name: string;
  SKU: string;
  GTINs: string[] | null;
  /** Public URL of the marketed physical package shot ("" until uploaded).
   * The one real image consumer surfaces (tap page) display. */
  PackageImageURL: string;
  /** Compliance class ("" or "general" | "medicine"). Gates which passport
   * fields apply and adds statutory notices on consumer pages. */
  RegulatoryCategory: string;
  CreatedAt: string;
  UpdatedAt: string;
}

export type AccessTier = "public" | "legitimate_interest" | "authorities";

export type DppStatus = "draft" | "published" | "deactivated";

export interface FieldValue {
  FieldKey: string;
  Value: unknown;
  Tier: AccessTier;
}

export interface DppVersion {
  ID: string;
  ProductID: string;
  OrgID: string;
  SchemaID: string;
  Status: DppStatus;
  Fields: FieldValue[] | null;
  PublishedAt: string | null;
  DeactivatedAt: string | null;
  DeactivationReason: string;
  CreatedAt: string;
  UpdatedAt: string;
}

export type FieldDataType = "text" | "number" | "boolean" | "json";

export interface FieldConfig {
  Key: string;
  Label: string;
  DefaultTier: AccessTier;
  Required: boolean;
  DataType: FieldDataType;
  /** Regulatory categories this field applies to; empty/absent = all. */
  Categories?: string[] | null;
}

export interface SchemaVersion {
  ID: string;
  Version: string;
  Fields: FieldConfig[];
  Active: boolean;
}

export interface UPI {
  ID: string;
  ProductID: string;
  OrgID: string;
  Code: string;
  GS1URL: string;
  SnapshotURL: string;
  CreatedAt: string;
}

export type CarrierType = "qr_code";

export interface Carrier {
  ID: string;
  UPIID: string;
  OrgID: string;
  CarrierType: CarrierType;
  Payload: string;
  CreatedAt: string;
}

// EBSI product-credential status (GET /ebsivc/products/{id}/credential).
// status "none" covers both "not issued yet" and "not visible to this org".
export interface EbsiCredential {
  status: "none" | "issued" | "failed" | "revoked";
  credentialId?: string;
  subjectDid?: string;
  statusReason?: string;
  issuedAt?: string;
  expiresAt?: string;
  verifyValid?: boolean;
  verifiedAt?: string;
}

// A GI authority's grant: one designation a consortium user may administer
// (GET /gi/authority-admins). Holding the gi_authority role without a grant
// confers nothing — the grant is the authorisation.
export interface AuthorityGrant {
  id: string;
  userId: string;
  giProductId: string;
  giName?: string;
  grantedBy?: string;
}

// Which GI licence a product is sold under
// (GET/PUT /gi/my/designations/{productId}). Licences are held per
// organization while passports are issued per product, so a producer holding
// more than one designation must state which applies. Until it does,
// `ambiguous` is true and the passport carries no GI claim at all.
export interface ProductDesignation {
  productId: string;
  giProducerId: string; // "" when no explicit choice was made
  giProductId?: string;
  giName?: string;
  resolved: boolean;
  ambiguous: boolean;
  options: DesignationOption[];
}

export interface DesignationOption {
  giProducerId: string;
  giProductId: string;
  giName: string;
  licenseNumber: string;
  region?: string;
  validUntil: string;
}

// Tap activity for the caller's org (GET /nfc/scans/summary?days=N):
// consumer engagement plus counterfeit signals, aggregated server-side.
export interface ScanDay {
  date: string; // YYYY-MM-DD (UTC)
  genuine: number;
  suspicious: number;
}

export interface ScanProduct {
  productId: string;
  genuine: number;
  suspicious: number;
}

export interface ScanAlert {
  tagId: string;
  productId: string;
  result: "invalid" | "replay" | "tampered";
  occurredAt: string;
}

/** A tag observed carrying two independently advancing counter sequences —
 * i.e. two physical chips. The counters are the evidence. */
export interface CloneEvidence {
  tagId: string;
  productId: string;
  counters: number[];
  firstAt: string;
  lastAt: string;
  leadingCounter: number;
}

/** A tag carrying a UID this platform never issued, which an operator has
 * established was impersonating your products. */
export interface ImpersonatingTag {
  id: string;
  uid: string;
  taps: number;
  firstSeen: string;
  lastSeen: string;
  status: string;
  suspectedProduct?: string;
  attributionNote?: string;
}

export interface ScanSummary {
  windowDays: number;
  total: number;
  genuine: number;
  suspicious: number;
  byResult: Record<string, number>;
  uniqueTags: number;
  daily: ScanDay[];
  topProducts: ScanProduct[];
  alerts: ScanAlert[];
  lastScanAt?: string;
  forgeryAttempts: number;
  clonedTags: CloneEvidence[];
  impersonatingTags: ImpersonatingTag[];
}

// The org's EBSI onboarding state (GET /provisioning/my/ebsi). "requested"
// means an org admin asked for onboarding and it awaits the platform operator.
export interface OrgEbsiStatus {
  status: "none" | "requested" | "onboarding" | "active" | "revoked";
  did?: string;
  statusReason?: string;
  requestedAt?: string;
  ebsiConfigured: boolean;
}

export type DocType =
  "declaration_of_conformity" | "test_report" | "certificate" | "other";

export const DOC_TYPES: { value: DocType; label: string }[] = [
  { value: "declaration_of_conformity", label: "Declaration of conformity" },
  { value: "test_report", label: "Test report" },
  { value: "certificate", label: "Certificate" },
  { value: "other", label: "Other" },
];

export interface DocumentMeta {
  ID: string;
  ProductID: string;
  OrgID: string;
  DocType: DocType;
  Filename: string;
  StorageKey: string;
  PublicURL: string;
  UploadedAt: string;
}

export interface Snapshot {
  ID: string;
  ProductID: string;
  OrgID: string;
  DppVersionID: string;
  SnapshotURL: string;
  JSONLDURL: string;
  CreatedAt: string;
}

// audit_log record (PocketBase base fields + this app's custom fields), read
// directly via the JS SDK — the audit module exposes no Go HTTP routes.
export interface AuditLogEntry {
  id: string;
  event_type: string;
  organization: string;
  payload: Record<string, unknown>;
  occurred_at: string;
  created: string;
}

// tenancy_users auth record (PocketBase base fields + this app's custom fields).
export interface TenancyUser {
  id: string;
  email: string;
  display_name: string;
  organization: string;
  operator_identifier: string;
  roles: string[];
  verified: boolean;
  created: string;
  updated: string;
}

export const TIER_LABELS: Record<AccessTier, string> = {
  public: "Public",
  legitimate_interest: "Legitimate interest",
  authorities: "Authorities only",
};

// --- NFC anti-counterfeit module (/api/dpp/nfc) ------------------------------

export type NfcVerifyResult = "genuine" | "invalid" | "replay" | "tampered";

export type NfcTerminalState =
  | "written_ok"
  | "write_failed"
  | "destroyed_at_write"
  | "destroyed_at_packaging";

// Reconciliation breakdown for a batch (server-computed): every provisioned UID
// is in exactly one terminal state, and
// Authorized = Remaining + WrittenOK + WriteFailed + Destroyed.
export interface NfcBatchProvisioning {
  Authorized: number;
  Provisioned: number;
  WrittenOK: number;
  WriteFailed: number;
  Destroyed: number;
  Remaining: number;
}

// A production batch: N units of one product authorized for provisioning.
export interface NfcBatch {
  ID: string;
  OrgID: string;
  ProductID: string;
  AuthorizedCount: number;
  Status: string;
  /** Production dates for the run. Zero-time when unset (legacy or durable goods). */
  ManufacturedAt: string;
  ExpiresAt: string;
  CreatedAt: string;
  Provisioning?: NfcBatchProvisioning;
}

// One NTAG 424 DNA chip bound to a batch/product. Per-tag SUN keys never leave
// the server, so they're absent here.
/** Consumer "suspicious item" report filed from the public tap page. */
export interface TapReport {
  id: string;
  uid: string;
  productId: string;
  tagId: string;
  verdict: string;
  note: string;
  status: "new" | "reviewed" | "dismissed";
  createdAt: string;
}

export interface NfcTag {
  ID: string;
  OrgID: string;
  UID: string;
  ProductID: string;
  BatchID: string;
  Status: string;
  CounterLastSeen: number;
  OriginalityVerified: boolean;
  TerminalState: NfcTerminalState;
  CreatedAt: string;
}

export const ROLE_LABELS: Record<MemberRole, string> = {
  admin: "Admin",
  editor: "Editor",
  viewer: "Viewer",
  supplier: "Supplier",
};

// --- GI registry module (/api/dpp/gi/*) --------------------------------------
// Go structs carry no json tags, so these mirror the PascalCase field names.

export type GiType = "PDO" | "PGI" | "TSG";
export type GiProductStatus = "active" | "pending" | "revoked";
export type GiLicenseStatus = "active" | "suspended" | "revoked";
export type GeocodingStatus = "unverified" | "verified" | "failed";

export const GI_TYPE_LABELS: Record<GiType, string> = {
  PDO: "PDO · Protected Designation of Origin",
  PGI: "PGI · Protected Geographical Indication",
  TSG: "TSG · Traditional Speciality Guaranteed",
};

// A registered Geographical Indication (platform-level reference data).
export interface GiProduct {
  ID: string;
  GiName: string;
  GiType: GiType;
  EuRegistrationNumber: string;
  TmdnURL: string;
  ProductCategory: string;
  ProductSpecification: string;
  Region: string;
  IssuingAuthority: string;
  Country: string;
  Status: GiProductStatus;
  CreatedAt: string;
  UpdatedAt: string;
}

// An organization authorized to make a GI product.
export interface GiProducer {
  ID: string;
  OrgID: string;
  OrgName: string;
  GiProductID: string;
  GiProductName: string;
  LicenseNumber: string;
  AuthorizedRegion: string;
  ValidFrom: string;
  ValidUntil: string;
  Status: GiLicenseStatus;
  Lat: number;
  Lng: number;
  FormattedAddress: string;
  PlaceID: string;
  GeocodingStatus: GeocodingStatus;
  LastGeocodedAt: string;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface GiProductDetail {
  product: GiProduct;
  producers: GiProducer[] | null;
}

// EBSI-compatible GI trust chain (/api/dpp/gi/trust-chain/{producerId}).
export type TrustRole = "RTAO" | "TAO" | "TI";

export const TRUST_ROLE_LABELS: Record<TrustRole, string> = {
  RTAO: "Root authority (RTAO)",
  TAO: "Accreditation body (TAO)",
  TI: "Trusted issuer (TI)",
};

export interface TrustChainLink {
  role: TrustRole;
  name: string;
  did: string;
  accreditationId: string;
  accreditationType: string;
  status: string;
  valid: boolean;
  issue?: string;
}

export interface TrustChain {
  subject: string;
  subjectDid?: string;
  giProductId?: string;
  giProductName?: string;
  trusted: boolean;
  broken?: string;
  links: TrustChainLink[];
}

// GeoJSON returned by /api/dpp/map/producers.
export interface GeoJsonFeature {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: Record<string, unknown>;
}

export interface GeoJsonFeatureCollection {
  type: "FeatureCollection";
  features: GeoJsonFeature[];
}
